"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import { submitForm } from "../../../../client";
import { GroundRichText } from "../GroundRichText/GroundRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full border border-weathered-steel bg-mineral-gray px-4 font-ground-body text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-basalt focus:border-oxidized-orange focus:ring-2 focus:ring-oxidized-orange/20 focus:ring-offset-2 focus:ring-offset-mineral-gray aria-[invalid=true]:border-oxidized-orange aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-oxidized-orange/20";

const autocompleteByType = {
  email: "email",
  phone: "tel",
  "short-text": "name",
} as const;

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await submitForm({
        fields: fields.map((field, index) => {
          const control = controls.find((item) => item.name === `field-${index}`);
          return {
            label: field.label,
            type: field.fieldType,
            value:
              control instanceof HTMLInputElement && control.type === "checkbox"
                ? control.checked
                : control?.value || "",
          };
        }),
        sourcePath: window.location.pathname,
      });
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
      window.requestAnimationFrame(() => failureRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="border border-primary bg-primary p-7 text-primary-foreground md:p-10"
        role="status"
      >
        <GroundRichText
          className="max-w-[65ch] text-primary-foreground [&_h3]:font-ground-display [&_h3]:text-3xl [&_h3]:font-bold"
          content={successMessage}
        />
      </div>
    );

  return (
    <form
      className="flex flex-col gap-6 border-t border-weathered-steel pt-6"
      noValidate
      onSubmit={submit}
      ref={formRef}
    >
      {submissionFailed ? (
        <div
          className="border border-oxidized-orange bg-mineral-gray p-5 outline-none focus-visible:ring-2 focus-visible:ring-oxidized-orange focus-visible:ring-offset-2"
          ref={failureRef}
          role="alert"
          tabIndex={-1}
        >
          <p className="font-ground-display text-xl font-bold">Your message has not been sent.</p>
          <GroundRichText className="mt-2 text-base text-foreground" content={errorMessage} />
          <button
            className="mt-4 inline-flex min-h-11 items-center border border-foreground px-4 font-ground-mono text-xs font-medium transition-colors hover:bg-foreground hover:text-mineral-gray focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            onClick={() => formRef.current?.requestSubmit()}
            type="button"
          >
            Try sending again
          </button>
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-ground-mono text-xs font-medium">
            {field.label}
            {field.required ? <span className="ml-1 text-basalt">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-weathered-steel accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                {<label htmlFor={inputId}>{label}</label>}
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select {...shared} className={controlClass} defaultValue="">
                    <option disabled value="">
                      {field.placeholder}
                    </option>
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    autoComplete={
                      field.fieldType in autocompleteByType
                        ? autocompleteByType[field.fieldType as keyof typeof autocompleteByType]
                        : undefined
                    }
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[+()0-9 .-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-ground-mono text-xs text-oxidized-orange" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="inline-flex min-h-12 w-full items-center justify-center border border-primary bg-primary px-6 font-ground-mono text-xs font-medium text-primary-foreground transition-colors hover:bg-charcoal-green-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
